import { Link } from 'react-router-dom';
import { ChevronRight, Clock, Users, Award, ThumbsUp } from 'lucide-react';
import { SEOHead, getBreadcrumbSchema } from './SEO';

function AboutPage() {
  return (
    <div className="min-h-screen bg-black pt-16" data-testid="about-page">
      <SEOHead
        title="About A2B Towing - Orangevale's Trusted Towing Company"
        description="A2B Towing - 10+ years serving Orangevale with professional towing, trained operators, and customer-first service. Call (916) 234-3923"
        canonical="/about"
        schema={getBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])}
      />

      <section className="pt-16 pb-20 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full">
              <span className="text-red-600 text-sm font-semibold">About Us</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-testid="about-heading">Orangevale's Most Trusted Towing Company</h1>
            <p className="text-xl text-gray-400">Providing reliable towing and roadside assistance services with a commitment to excellence and customer satisfaction.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-400 text-lg leading-relaxed">
                <p>A2B Towing & Roadside Assistance started with a simple goal - to be the most trusted towing company in Orangevale, CA. Our team brings years of experience in emergency towing and roadside help.</p>
                <p>We understand that vehicle breakdowns and accidents are stressful situations. That's why we focus on quick response times and putting our customers first. When you call A2B Towing, you're not just getting a tow truck - you're getting a team of professionals who genuinely care about getting you back on the road safely.</p>
                <p>As a leading local towing service in Orangevale, we stand out through our focus on quality work and great customer care. Our tow truck operators are trained professionals who handle every job with care and skill.</p>
              </div>
            </div>
            <div className="relative">
              <img alt="A2B Towing professional team with modern tow trucks serving Orangevale and Sacramento County" className="rounded-lg shadow-2xl w-full h-[500px] object-cover" src="https://images.pexels.com/photos/7997502/pexels-photo-7997502.jpeg" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full">
              <span className="text-red-600 text-sm font-semibold">Our Values</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">What Sets Us Apart</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: "Always Available", desc: "We're ready to help when you need us most, day or night, weekends and holidays." },
              { icon: Users, title: "Professional Team", desc: "Trained and experienced tow truck operators who handle every job with care." },
              { icon: Award, title: "Quality Service", desc: "We take pride in delivering exceptional service on every call." },
              { icon: ThumbsUp, title: "Customer First", desc: "Your satisfaction and safety are our top priorities." },
            ].map(v => (
              <div key={v.title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center hover:border-red-600 transition-all group">
                <div className="w-16 h-16 bg-red-600/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                  <v.icon className="w-8 h-8 text-red-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{v.title}</h3>
                <p className="text-gray-400">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { val: "10+", label: "Years Experience" },
              { val: "5000+", label: "Happy Customers" },
              { val: "100%", label: "Customer Satisfaction" },
              { val: "30-45min", label: "Avg Response Time" },
            ].map(s => (
              <div key={s.label}>
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">{s.val}</div>
                <div className="text-gray-400 text-lg">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-20 bg-zinc-950" data-testid="maps-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Find Us in Orangevale</h2>
            <p className="text-gray-400">Serving Orangevale and all of Sacramento County</p>
          </div>
          <div className="rounded-xl overflow-hidden border border-zinc-800 max-w-4xl mx-auto">
            <iframe
              title="A2B Towing Location - Orangevale CA"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d49861.89299867382!2d-121.25978574831716!3d38.68601997891458!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809adf2bcc455399%3A0xe8b2e98e7f8b1e42!2sOrangevale%2C%20CA%2095662!5e0!3m2!1sen!2sus!4v1706000000000!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-maps-embed"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-red-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Experience the A2B Difference?</h2>
          <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">Join thousands of satisfied customers who trust A2B Towing for all their towing and roadside assistance needs.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19162343923"><button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-md transition-all hover:scale-105">Call Us Now</button></a>
            <Link to="/quote"><button className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold rounded-md transition-all inline-flex items-center gap-2">Request Service <ChevronRight className="w-5 h-5" /></button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
